<template>
  <v-container>
    <v-layout row>
      <v-flex xs12 sm8 offset-sm2>

        <!-- Back link -->
        <router-link v-if="dancerIdentified" to="/" class="back-link">&larr; Back to Dashboard</router-link>

        <!-- Loading -->
        <v-progress-circular v-if="loading" indeterminate color="primary" class="mt-5"></v-progress-circular>

        <!-- Hidden until this year's corps costumes exist -->
        <span v-else-if="!costumeOrderOpen">
          <h2>IABF Corps de Ballet</h2>
          <p>We're still putting together this year's costumes. Check back when corps ordering opens — we'll have it ready for you.</p>
        </span>

        <!-- Dancer Lookup -->
        <span v-else-if="!dancerIdentified">
          <h2>IABF Corps de Ballet</h2>
          <p>Please log in to access your costume order form.</p>
          <form @submit.prevent="onLookUp">
            <v-text-field
              v-model="lookupEmail"
              label="Email"
              type="email"
              required
            />
            <v-text-field
              v-model="lookupLast4"
              label="Last 4 Digits of Phone #"
              type="text"
              maxlength="4"
              required
            />
            <p v-if="lookupError" style="color: red;">We couldn't find your registration. Please double-check your email and last 4 digits of your phone number.</p>
            <v-btn
              class="primary"
              :disabled="!lookupFormValid"
              type="submit"
            >
              Look Up My Registration
            </v-btn>
          </form>
        </span>

        <!-- Not in Corps -->
        <span v-else-if="notInCorps">
          <h2>IABF Corps de Ballet</h2>
          <h3>Welcome, {{ firstName }}!</h3>
          <p>Nothing to worry about here — this section is for the Corps de Ballet costume orders. You're all set!</p>
          <p>If you have any questions, feel free to reach out at <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.</p>
        </span>

        <!-- Order Already Completed -->
        <span v-else-if="costumeOrderCompleted && currentStep === 0">
          <h2>IABF Corps de Ballet</h2>
          <h3>Welcome back, {{ firstName }}!</h3>
          <p>Your costume order has been submitted. Here's a summary of your selections:</p>

          <div class="order-summary-readonly">
            <div v-if="waltzOfHours.participating">
              <h4>Waltz of the Hours</h4>
              <ul>
                <li>Pancake Tutu + Bodice: {{ tutuChoiceLabel(waltzOfHours.tutuChoice) }}</li>
                <li v-if="waltzOfHours.tutuChoice === 'own'">Color: {{ waltzOfHours.tutuColor }}</li>
                <li v-if="assignedWaltzColor">
                  Your Assigned Color: <strong>{{ assignedWaltzColor }}</strong>
                </li>
                <li v-else-if="waltzOfHours.tutuChoice === 'buy' || waltzOfHours.tutuChoice === 'borrow'">
                  Color Preferences: 1. {{ waltzOfHours.colorPref1 }}, 2. {{ waltzOfHours.colorPref2 }}, 3. {{ waltzOfHours.colorPref3 }}
                </li>
                <li>Learned on Broche on-demand: {{ waltzOfHours.learnedOnDemand }}</li>
                <li>Pirouette choice: {{ waltzOfHours.pirouetteChoice }}</li>
              </ul>
            </div>

            <div v-if="aliceInWonderland.participating">
              <h4>Alice in Wonderland</h4>
              <ul>
                <li>Role: {{ aliceInWonderland.role }}</li>
                <li v-if="aliceInWonderland.role === 'Queen of Hearts'">
                  Costume: To be coordinated individually
                </li>
                <li v-else-if="aliceInWonderland.role !== 'Men & Jokers'">
                  Costume: {{ tutuChoiceLabel(aliceInWonderland.tutuChoice) }}
                </li>
              </ul>
            </div>

            <div v-if="becoming.participating">
              <h4>Becoming</h4>
              <ul>
                <li>Top: {{ becoming.topChoice === 'crop' ? 'Crop top ($50)' : becoming.topChoice === 'crop_ship' ? 'Crop top + shipping ($65)' : 'Full coverage leotard' }}</li>
                <li v-if="becoming.cropTopSize">Crop Top Size: {{ becoming.cropTopSize }}</li>
                <li>Blue Skirt: {{ becoming.skirtChoice === 'buy' ? 'Buy ($65)' : 'Borrow ($0)' }}</li>
                <li v-if="becoming.skirtSize">Skirt Size: {{ becoming.skirtSize }}</li>
              </ul>
            </div>

            <div v-if="garlandWaltz.participating">
              <h4>Garland Waltz</h4>
              <ul>
                <li>Role: {{ garlandWaltz.role }}</li>
                <li v-if="garlandWaltz.role === 'Ladies' && !garlandWaltz.tutuSharedWithAlice">
                  White Romantic Tutu: {{ tutuChoiceLabel(garlandWaltz.tutuChoice) }}
                </li>
                <li v-if="garlandWaltz.role === 'Ladies' && garlandWaltz.tutuSharedWithAlice">
                  White Romantic Tutu: Same as Alice in Wonderland
                </li>
                <li v-if="garlandWaltz.role === 'Ladies' && assignedGarlandColor">
                  Your Assigned Color: <strong>{{ assignedGarlandColor }}</strong>
                </li>
                <li v-else-if="garlandWaltz.role === 'Ladies'">
                  Sash Color Preferences: 1. {{ garlandWaltz.colorPref1 }}, 2. {{ garlandWaltz.colorPref2 }}, 3. {{ garlandWaltz.colorPref3 }}
                </li>
              </ul>
            </div>

            <h4>Total: ${{ costumeTotalCost }}</h4>
            <p v-if="costumeOrderPaid">Payment: Paid</p>
            <span v-else-if="costumeTotalCost > 0">
              <p>Payment: Unpaid</p>
              <v-btn class="primary mr-3" @click="payNow" :loading="paymentLoading">
                Pay Now (${{ costumeTotalCost }})
              </v-btn>
              <v-btn @click="markAddedToNextPayment">
                Add to Next IABF Payment
              </v-btn>
              <p v-if="addedToNextPaymentConfirmed" class="mt-2" style="color: green; font-weight: bold;">Added to your next IABF payment.</p>
            </span>
          </div>

          <p class="mt-4"><i>Please contact <a href="mailto:hello@iabf.dance">hello@iabf.dance</a> by February 28 to make any changes.</i></p>

          <v-btn v-if="dancerEmail === 'julie@brocheballet.com'" class="mt-4" color="error" @click="resetOrder">
            Reset & Retake Form (Testing)
          </v-btn>
        </span>

        <!-- Main Form -->
        <span v-else-if="dancerIdentified">

          <!-- Landing -->
          <span v-if="currentStep === 0">
            <h2>IABF Corps de Ballet</h2>
            <h3>Welcome, {{ firstName }}!</h3>
            <p>We are so excited to perform with you! Let's put together your perfect costume.</p>
            <p>Our goal is keeping costume costs low while ensuring everyone looks stunning on stage. You'll go through each piece, see exactly what's provided and what you need, and select your options. If cost is prohibitive for any item or part of your costume, please reach out at <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.</p>
            <v-btn class="primary" @click="currentStep = 1">
              Start Costume Order
            </v-btn>
          </span>

          <!-- Step 1: Waltz of the Hours -->
          <span v-if="currentStep === 1">
            <v-form ref="step1Form">
            <h3>Step 1 of 7: Waltz of the Hours</h3>
            <p class="piece-tagline">Elegant. Fast. Exhilarating.</p>
            <p class="piece-description">Classical Ballet excerpt from <i>Coppelia</i>, choreographed by Julie Leung. This piece is filled with musicality, speed, and classic Corps de Ballet precision. The choreography offers a thrilling challenge for dancers who love quick movement, sharp musical timing, and the ethereal lightness of a Romantic waltz.</p>

            <p class="form-question">Are you in Waltz of the Hours?</p>
            <v-radio-group v-model="waltzOfHours.participating">
              <v-radio label="Yes" :value="true"></v-radio>
              <v-radio label="No" :value="false"></v-radio>
            </v-radio-group>

            <span v-if="waltzOfHours.participating">
              <img src="/static/costume-vision-waltz.png" alt="Costume Vision - Waltz of the Hours" class="costume-vision-img" />

              <div class="costume-info-box">
                <p class="costume-info-header">Costume Details for This Piece</p>
                <p><strong>Provided by IABF:</strong></p>
                <ul>
                  <li>Pancake Tutu + Bodice in your chosen color (buy your own to keep or borrow from IABF)</li>
                  <li>Matching Floral & Tulle Headpiece</li>
                </ul>
                <p><strong>You Provide:</strong></p>
                <ul>
                  <li>(Optional) Nude leotard for coverage and confidence under tutu</li>
                  <li>Pink or skin-tone tights with matching ballet flats or pointe shoes</li>
                </ul>
                <p class="cost-note"><i>If cost is prohibitive for any item or part of your costume, please reach out at <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.</i></p>
              </div>

              <p class="form-question">Pancake Tutu + Bodice</p>
              <div class="tutu-detail-box">
                <img src="/static/tutu-colorful-pancake.jpg" alt="Colorful Pancake Tutu + Bodice" class="tutu-detail-img" />
                <p>Beautiful professional pancake tutus with an attached corset bodice. For ease of sizing, the bodice laces up with a drawstring corset. The tutu and bodice are decorated with glitter and gems. You are welcome to borrow from IABF's costume wardrobe, or buy your own to take home and keep. If you borrow from our wardrobe, we will find the best fit based on your measurements. Your tutu will be waiting for you at IABF — no need to bring it yourself!</p>
              </div>
              <v-radio-group v-model="waltzOfHours.tutuChoice" :rules="[v => !!v || 'Please select a tutu option.']">
                <v-radio label="Use my own (from past IABF Don Quixote or Sleeping Beauty)" value="own"></v-radio>
                <v-radio label="Buy new ($275)" value="buy"></v-radio>
                <v-radio label="Borrow ($0)" value="borrow"></v-radio>
              </v-radio-group>
              <p v-if="waltzOfHours.tutuChoice === 'buy'" class="return-policy"><i>Tutus are custom made to order and cannot be returned, refunded, or exchanged.</i></p>

              <span v-if="waltzOfHours.tutuChoice === 'own'">
                <v-select
                  v-model="waltzOfHours.tutuColor"
                  :items="waltzColorOptions"
                  label="What color is your tutu?"
                  :rules="[v => !!v || 'Please select your tutu color.']"
                ></v-select>
              </span>

              <span v-if="waltzOfHours.tutuChoice === 'borrow'">
                <p class="note"><i>Note: Borrowed tutus are subject to availability and fit. Please provide your ranked top 3 color choices below.</i></p>
              </span>

              <span v-if="waltzOfHours.tutuChoice === 'buy' || waltzOfHours.tutuChoice === 'borrow'">
                <p class="form-question">Ranked top 3 color choices:</p>
                <p class="note"><i>Colors will be assigned by mid-March.</i></p>
                <v-select v-model="waltzOfHours.colorPref1" :items="waltzColorOptions1" label="1st Choice" :rules="[v => !!v || 'Required.']"></v-select>
                <v-select v-model="waltzOfHours.colorPref2" :items="waltzColorOptions2" label="2nd Choice" :rules="[v => !!v || 'Required.']"></v-select>
                <v-select v-model="waltzOfHours.colorPref3" :items="waltzColorOptions3" label="3rd Choice" :rules="[v => !!v || 'Required.']"></v-select>
              </span>

              <span v-if="waltzOfHours.tutuChoice === 'buy' || waltzOfHours.tutuChoice === 'borrow'">
                <p class="form-question">Measurements</p>
                <v-radio-group v-model="sharedMeasurements.unit" row>
                  <v-radio label="Inches" value="inches"></v-radio>
                  <v-radio label="Centimeters" value="cm"></v-radio>
                </v-radio-group>
                <v-text-field v-model="sharedMeasurements.bust" label="Bust" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small>mdi-information</v-icon>
                      </template>
                      <span>Measure around the fullest part of your bust</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <v-text-field v-model="sharedMeasurements.waist" label="Waist" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small>mdi-information</v-icon>
                      </template>
                      <span>Measure around your natural waistline (narrowest part)</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <v-text-field v-model="sharedMeasurements.hip" label="Hip" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small>mdi-information</v-icon>
                      </template>
                      <span>Measure around the fullest part of your hips</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <v-text-field v-model="sharedMeasurements.girth" label="Girth" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small>mdi-information</v-icon>
                      </template>
                      <span>Measure from the front of your shoulder, down through the crotch, and back up to the same shoulder</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <v-text-field v-model="sharedMeasurements.height" label="Height" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small>mdi-information</v-icon>
                      </template>
                      <span>Your full standing height</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </span>

              <p class="form-question">Have you learned Waltz of the Hours on Broche on-demand?</p>
              <v-radio-group v-model="waltzOfHours.learnedOnDemand" :rules="[v => !!v || 'Please answer this question.']">
                <v-radio label="Yes" value="yes"></v-radio>
                <v-radio label="No" value="no"></v-radio>
                <v-radio label="Not sure" value="not sure"></v-radio>
              </v-radio-group>

              <p class="form-question">In the choreography, there is a pirouette landing in a lunge on 1 knee. Do you want to do a pirouette to the knee or stay standing?</p>
              <v-radio-group v-model="waltzOfHours.pirouetteChoice" :rules="[v => !!v || 'Please answer this question.']">
                <v-radio label="Pirouette to knee" value="knee"></v-radio>
                <v-radio label="Stay standing" value="standing"></v-radio>
              </v-radio-group>
            </span>

            <div v-if="measurementWarnings.length > 0 && waltzOfHours.participating && (waltzOfHours.tutuChoice === 'buy' || waltzOfHours.tutuChoice === 'borrow')" class="measurement-warnings">
              <p v-for="(warning, i) in measurementWarnings" :key="i">{{ warning }}</p>
            </div>

            <div class="step-nav">
              <v-btn @click="currentStep = 0">Back</v-btn>
              <v-btn class="primary" @click="goToStep(2, 'step1Form')">Next</v-btn>
            </div>
            </v-form>
          </span>

          <!-- Step 2: Alice in Wonderland -->
          <span v-if="currentStep === 2">
            <v-form ref="step2Form">
            <h3>Step 2 of 7: Alice in Wonderland</h3>
            <p class="piece-tagline">Playful. Energetic. Dramatic.</p>
            <p class="piece-description">Neoclassical Ballet excerpt from <i>Alice in Wonderland</i>, choreographed by Kelsey Maloney-Steiner. Step into the whimsical world of Alice with this bold, theatrical piece featuring the commanding Queen of Hearts, mischievous Jokers, and a beautifully unified Cards Corps de Ballet. Expect playful energy, dramatic accents, and a joyful chance to embody an imaginative world!</p>

            <p class="form-question">Are you in Alice in Wonderland?</p>
            <v-radio-group v-model="aliceInWonderland.participating">
              <v-radio label="Yes" :value="true"></v-radio>
              <v-radio label="No" :value="false"></v-radio>
            </v-radio-group>

            <span v-if="aliceInWonderland.participating">
              <img src="/static/costume-vision-alice.png" alt="Costume Vision - Alice in Wonderland" class="costume-vision-img" />

              <p class="form-question">Which role?</p>
              <v-radio-group v-model="aliceInWonderland.role" :rules="[v => !!v || 'Please select your role.']">
                <v-radio label="Cards Corps - Pointe & Pancake Tutus" value="Pointe"></v-radio>
                <v-radio label="Queen of Hearts (Intermediate Pointe Solo)" value="Queen of Hearts"></v-radio>
                <v-radio label="Cards Corps - Flat Shoes & Romantic Tutus" value="Flat Ladies"></v-radio>
                <v-radio label="Men & Jokers" value="Men & Jokers"></v-radio>
              </v-radio-group>

              <!-- Pointe / Pancake Tutus -->
              <span v-if="aliceInWonderland.role === 'Pointe'">
                <div class="costume-info-box">
                  <p class="costume-info-header">Costume Details - Cards Corps (Pointe)</p>
                  <p><strong>Provided by IABF:</strong></p>
                  <ul>
                    <li>White Pancake Tutu (buy your own to keep or borrow from IABF)</li>
                    <li>Playing-Card Overlay Accessories</li>
                    <li>Coordinated Headpiece</li>
                  </ul>
                  <p><strong>You Provide:</strong></p>
                  <ul>
                    <li>White leotard — this can be any white leotard of your choosing. Corps Dancewear and SoDanca both have popular white leotard options with thick lining, but you are welcome to choose what fits and flatters you best.</li>
                    <li>Pink or skin-tone tights with matching pointe shoes</li>
                  </ul>
                  <p class="cost-note"><i>If cost is prohibitive for any item or part of your costume, please reach out at <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.</i></p>
                </div>

                <p class="form-question">White Pancake Tutu</p>
                <div class="tutu-detail-box">
                  <img src="/static/tutu-white-pancake.jpg" alt="White Pancake Tutu" class="tutu-detail-img" />
                  <p>A white pancake tutu made from stiff tulle and decorated with beautiful silver embellishments. This is only the tutu (not the bodice), making it a very versatile piece of your costume wardrobe. The waistband is elastic and has an elastic panty with ruffle. You are welcome to borrow from IABF's costume wardrobe, or buy your own to take home and keep. If you borrow from our wardrobe, we will find the best fit based on your measurements. Your tutu will be waiting for you at IABF — no need to bring it yourself!</p>
                </div>
                <v-radio-group v-model="aliceInWonderland.tutuChoice" :rules="[v => !!v || 'Please select a tutu option.']">
                  <v-radio label="Use my own" value="own"></v-radio>
                  <v-radio label="Buy ($75)" value="buy"></v-radio>
                  <v-radio label="Borrow ($0)" value="borrow"></v-radio>
                </v-radio-group>
                <p v-if="aliceInWonderland.tutuChoice === 'buy'" class="return-policy"><i>Tutus are custom made to order and cannot be returned, refunded, or exchanged.</i></p>

                <span v-if="aliceInWonderland.tutuChoice === 'buy' || aliceInWonderland.tutuChoice === 'borrow'">
                  <p class="form-question">Measurements</p>
                  <v-radio-group v-model="sharedMeasurements.unit" row>
                    <v-radio label="Inches" value="inches"></v-radio>
                    <v-radio label="Centimeters" value="cm"></v-radio>
                  </v-radio-group>
                  <v-text-field v-model="sharedMeasurements.waist" label="Waist" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                    <template v-slot:append>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" small>mdi-information</v-icon>
                        </template>
                        <span>Measure around your natural waistline (narrowest part)</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                  <v-text-field v-model="sharedMeasurements.hip" label="Hip" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                    <template v-slot:append>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" small>mdi-information</v-icon>
                        </template>
                        <span>Measure around the fullest part of your hips</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                  <v-text-field v-model="sharedMeasurements.halfGirth" label="1/2 Girth" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                    <template v-slot:append>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" small>mdi-information</v-icon>
                        </template>
                        <span>Measure from your belly button through the legs up your lower back to the same height as the belly button</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                  <v-text-field v-model="sharedMeasurements.height" label="Height" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                    <template v-slot:append>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" small>mdi-information</v-icon>
                        </template>
                        <span>Your full standing height</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </span>
              </span>

              <!-- Flat Ladies / Romantic Tutus -->
              <span v-if="aliceInWonderland.role === 'Flat Ladies'">
                <div class="costume-info-box">
                  <p class="costume-info-header">Costume Details - Cards Corps (Flat Shoes)</p>
                  <p><strong>Provided by IABF:</strong></p>
                  <ul>
                    <li>White Romantic Tutu (buy your own to keep or borrow from IABF)</li>
                    <li>Playing-Card Overlay Accessories</li>
                    <li>Coordinated Headpiece</li>
                  </ul>
                  <p><strong>You Provide:</strong></p>
                  <ul>
                    <li>White leotard — this can be any white leotard of your choosing. Corps Dancewear and SoDanca both have popular white leotard options with thick lining, but you are welcome to choose what fits and flatters you best.</li>
                    <li>Pink or skin-tone tights with matching ballet flats or pointe shoes</li>
                  </ul>
                  <p class="cost-note"><i>If cost is prohibitive for any item or part of your costume, please reach out at <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.</i></p>
                </div>

                <p class="form-question">White Romantic Tutu</p>
                <div class="tutu-detail-box">
                  <img src="/static/tutu-white-romantic.jpg" alt="White Romantic Tutu" class="tutu-detail-img" />
                  <p>A white romantic tutu featuring several layers of soft tulle that hit below the knee, with an elastic waistband and panty. This beautiful romantic tutu floats and twirls with your every movement. You are welcome to borrow from IABF's costume wardrobe, or buy your own to take home and keep. If you borrow from our wardrobe, we will find the best fit based on your measurements. Your tutu will be waiting for you at IABF — no need to bring it yourself!</p>
                </div>
                <v-radio-group v-model="aliceInWonderland.tutuChoice" :rules="[v => !!v || 'Please select a tutu option.']">
                  <v-radio label="Use my own from past IABF Waltz of Snowflakes or Giselle Wilis" value="own"></v-radio>
                  <v-radio label="Buy ($75)" value="buy"></v-radio>
                  <v-radio label="Borrow ($0)" value="borrow"></v-radio>
                </v-radio-group>
                <p v-if="aliceInWonderland.tutuChoice === 'buy'" class="return-policy"><i>Tutus are custom made to order and cannot be returned, refunded, or exchanged.</i></p>

                <span v-if="aliceInWonderland.tutuChoice === 'buy' || aliceInWonderland.tutuChoice === 'borrow'">
                  <p class="form-question">Measurements</p>
                  <v-radio-group v-model="sharedMeasurements.unit" row>
                    <v-radio label="Inches" value="inches"></v-radio>
                    <v-radio label="Centimeters" value="cm"></v-radio>
                  </v-radio-group>
                  <v-text-field v-model="sharedMeasurements.waist" label="Waist" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                    <template v-slot:append>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" small>mdi-information</v-icon>
                        </template>
                        <span>Measure around your natural waistline (narrowest part)</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                  <v-text-field v-model="sharedMeasurements.hip" label="Hip" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                    <template v-slot:append>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" small>mdi-information</v-icon>
                        </template>
                        <span>Measure around the fullest part of your hips</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                  <v-text-field v-model="sharedMeasurements.halfGirth" label="1/2 Girth" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                    <template v-slot:append>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" small>mdi-information</v-icon>
                        </template>
                        <span>Measure from your belly button through the legs up your lower back to the same height as the belly button</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                  <v-text-field v-model="sharedMeasurements.height" label="Height" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                    <template v-slot:append>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" small>mdi-information</v-icon>
                        </template>
                        <span>Your full standing height</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </span>
              </span>

              <!-- Queen of Hearts -->
              <span v-if="aliceInWonderland.role === 'Queen of Hearts'">
                <p class="note"><i>We'll get in touch with you individually to coordinate costume options!</i></p>
              </span>

              <!-- Men & Jokers -->
              <span v-if="aliceInWonderland.role === 'Men & Jokers'">
                <div class="costume-info-box">
                  <p class="costume-info-header">Costume Details - Men & Jokers</p>
                  <p><strong>Provided by IABF:</strong></p>
                  <ul>
                    <li>Playing-Card Overlay Accessories</li>
                  </ul>
                  <p><strong>You Provide:</strong></p>
                  <ul>
                    <li>Black tights</li>
                    <li>White ballet shirt</li>
                    <li>Black ballet flats</li>
                  </ul>
                  <p class="cost-note"><i>If cost is prohibitive for any item or part of your costume, please reach out at <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.</i></p>
                </div>
                <p class="note"><i>No costume purchase needed for this role. Just bring the items listed above!</i></p>
              </span>
            </span>

            <div v-if="measurementWarnings.length > 0 && aliceInWonderland.participating && aliceInWonderland.role && aliceInWonderland.role !== 'Men & Jokers'" class="measurement-warnings">
              <p v-for="(warning, i) in measurementWarnings" :key="i">{{ warning }}</p>
            </div>

            <div class="step-nav">
              <v-btn @click="currentStep = 1">Back</v-btn>
              <v-btn class="primary" @click="goToStep(3, 'step2Form')">Next</v-btn>
            </div>
            </v-form>
          </span>

          <!-- Step 3: Le Jazz Hot -->
          <span v-if="currentStep === 3">
            <h3>Step 3 of 7: Le Jazz Hot</h3>
            <p class="piece-tagline">Sharp. Sultry. Iconic.</p>
            <p class="piece-description">Fosse Jazz excerpt from <i>Victor/Victoria</i>, choreographed by Sean Kelly. A dynamic, Fosse-inspired piece celebrating clean lines, rhythmic precision, attitude, and unmistakable jazz flair. Expect stylized movements, intentional isolations, low-level footwork, and bold performance quality.</p>

            <p class="form-question">Are you in Le Jazz Hot?</p>
            <v-radio-group v-model="leJazzHot.participating">
              <v-radio label="Yes" :value="true"></v-radio>
              <v-radio label="No" :value="false"></v-radio>
            </v-radio-group>

            <span v-if="leJazzHot.participating">
              <div class="costume-info-box">
                <p class="costume-info-header">Costume Details - Le Jazz Hot</p>
                <p>The look for this piece is classic Fosse: minimalistic, bold, unmistakably chic.</p>
                <p><strong>Provided by IABF:</strong></p>
                <ul>
                  <li>Nothing - this piece is entirely self-costumed</li>
                </ul>
                <p><strong>You Provide - All Black, Head to Toe:</strong></p>
                <ul>
                  <li>Black top (tank, leotard, fitted tee, or jazz top)</li>
                  <li>Black pants, leggings, shorts, or unitard</li>
                  <li>Black jazz shoes or black ballet shoes</li>
                </ul>
                <p class="note"><i>Keep everything fitted, clean, and movement-friendly so the crisp Fosse-style isolations and shapes shine through.</i></p>
                <p class="cost-note"><i>If cost is prohibitive for any item or part of your costume, please reach out at <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.</i></p>
              </div>
              <p class="note"><i>No action needed here - just be sure to pack your all-black outfit!</i></p>
            </span>

            <div class="step-nav">
              <v-btn @click="currentStep = 2">Back</v-btn>
              <v-btn class="primary" @click="currentStep = 4">Next</v-btn>
            </div>
          </span>

          <!-- Step 4: Becoming -->
          <span v-if="currentStep === 4">
            <v-form ref="step4Form">
            <h3>Step 4 of 7: Becoming</h3>
            <p class="piece-tagline">Expression. Freedom. Awakening.</p>
            <p class="piece-description">Contemporary piece set to the <i>Interstellar Main Theme</i>, choreographed by Cindy Heen. A deeply expressive work where language falls short and movement steps forward. The music holds a haunting blend of urgency and stillness, reminding us of the wonder of being fully alive in this moment.</p>

            <p class="form-question">Are you in Becoming?</p>
            <v-radio-group v-model="becoming.participating">
              <v-radio label="Yes" :value="true"></v-radio>
              <v-radio label="No" :value="false"></v-radio>
            </v-radio-group>

            <span v-if="becoming.participating">
              <img src="/static/costume-vision-becoming.png" alt="Costume Vision - Becoming" class="costume-vision-img" />

              <div class="costume-info-box">
                <p class="costume-info-header">Costume Details - Becoming</p>
                <p>Soft, flowing design supports the emotional, contemporary quality of this work. This piece is performed barefoot.</p>
                <ul>
                  <li>Blue Flowing Skirt - lightweight, ethereal skirt enhancing sweeping, breath-driven movement (buy your own to keep or borrow from IABF)</li>
                  <li>Your choice of crop top (ordered through us) or leotard (details sent soon)</li>
                  <li>Barefoot - no shoes needed</li>
                </ul>
                <p class="cost-note"><i>If cost is prohibitive for any item or part of your costume, please reach out at <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.</i></p>
              </div>

              <p class="form-question">Choice of Blue top:</p>
              <v-radio-group v-model="becoming.topChoice" :rules="[v => !!v || 'Please select a top option.']">
                <v-radio label="Crop top - to be picked up at IABF ($50)" value="crop"></v-radio>
                <v-radio label="Crop top - buy and ship directly to me to try on and decide (USA only) ($65)" value="crop_ship"></v-radio>
                <v-radio label="Full coverage leotard - details for which leotard to get will be sent out soon" value="leotard"></v-radio>
              </v-radio-group>
              <p v-if="becoming.topChoice === 'crop'" class="return-policy"><i>Crop tops and skirts are custom made to order and cannot be returned, refunded, or exchanged.</i></p>
              <p v-else-if="becoming.topChoice === 'crop_ship'" class="return-policy"><i>Crop Top & Skirt may be exchanged for a different size for $5 + Shipping. Refunds are not offered.</i></p>

              <span v-if="becoming.topChoice === 'crop_ship'">
                <p class="form-question">Shipping Address</p>
                <v-text-field v-model="becoming.shippingName" label="Full Name" :rules="[v => !!v || 'Required.']"></v-text-field>
                <v-text-field v-model="becoming.shippingAddress1" label="Address Line 1" :rules="[v => !!v || 'Required.']"></v-text-field>
                <v-text-field v-model="becoming.shippingAddress2" label="Address Line 2 (optional)"></v-text-field>
                <v-text-field v-model="becoming.shippingCity" label="City" :rules="[v => !!v || 'Required.']"></v-text-field>
                <v-text-field v-model="becoming.shippingState" label="State" :rules="[v => !!v || 'Required.']"></v-text-field>
                <v-text-field v-model="becoming.shippingZip" label="ZIP Code" :rules="[v => !!v || 'Required.']"></v-text-field>
              </span>

              <span v-if="becoming.topChoice === 'crop' || becoming.topChoice === 'crop_ship'">
                <p class="form-question">Crop Top Size</p>
                <p class="note"><i>Use the size chart below to find your best fit:</i></p>
                <v-btn-toggle v-model="becomingSizeChartUnit" mandatory class="mb-3">
                  <v-btn small value="inches">Inches</v-btn>
                  <v-btn small value="cm">Centimeters</v-btn>
                </v-btn-toggle>
                <div class="size-chart-container">
                  <img v-if="becomingSizeChartUnit === 'inches'" src="/static/becoming-size-chart-inches.png" alt="Becoming Size Chart (Inches)" class="size-chart-img" />
                  <img v-else src="/static/becoming-size-chart-cm.png" alt="Becoming Size Chart (Centimeters)" class="size-chart-img" />
                </div>
                <v-select
                  v-model="becoming.cropTopSize"
                  :items="becomingSizeOptions"
                  label="Preferred crop top size"
                  :rules="[v => !!v || 'Please select a size.']"
                ></v-select>
              </span>

              <p class="form-question">Blue Flowing Skirt:</p>
              <v-radio-group v-model="becoming.skirtChoice" :rules="[v => !!v || 'Please select a skirt option.']">
                <v-radio label="Buy ($65)" value="buy"></v-radio>
                <v-radio label="Borrow ($0)" value="borrow"></v-radio>
              </v-radio-group>
              <p v-if="becoming.skirtChoice === 'buy' && becoming.topChoice === 'crop'" class="return-policy"><i>Crop tops and skirts are custom made to order and cannot be returned, refunded, or exchanged.</i></p>
              <p v-else-if="becoming.skirtChoice === 'buy'" class="return-policy"><i>Crop Top & Skirt may be exchanged for a different size for $5 + Shipping. Refunds are not offered.</i></p>

              <span v-if="becoming.skirtChoice === 'buy' || becoming.skirtChoice === 'borrow'">
                <p class="form-question">Skirt Size</p>
                <p class="note"><i>Use the size chart below to find your best fit:</i></p>
                <v-btn-toggle v-model="becomingSizeChartUnit" mandatory class="mb-3">
                  <v-btn small value="inches">Inches</v-btn>
                  <v-btn small value="cm">Centimeters</v-btn>
                </v-btn-toggle>
                <div class="size-chart-container">
                  <img v-if="becomingSizeChartUnit === 'inches'" src="/static/becoming-size-chart-inches.png" alt="Becoming Size Chart (Inches)" class="size-chart-img" />
                  <img v-else src="/static/becoming-size-chart-cm.png" alt="Becoming Size Chart (Centimeters)" class="size-chart-img" />
                </div>
                <v-select
                  v-model="becoming.skirtSize"
                  :items="becomingSizeOptions"
                  label="Preferred skirt size"
                  :rules="[v => !!v || 'Please select a size.']"
                ></v-select>
              </span>
            </span>

            <div class="step-nav">
              <v-btn @click="currentStep = 3">Back</v-btn>
              <v-btn class="primary" @click="goToStep(5, 'step4Form')">Next</v-btn>
            </div>
            </v-form>
          </span>

          <!-- Step 5: Garland Waltz -->
          <span v-if="currentStep === 5">
            <v-form ref="step5Form">
            <h3>Step 5 of 7: Garland Waltz</h3>
            <p class="piece-tagline">Graceful. Lyrical. Timeless.</p>
            <p class="piece-description">Classical Ballet excerpt from <i>Sleeping Beauty</i>, choreographed by Jena Kocsis. One of the most beloved classical ensemble dances in the ballet repertoire, filled with sweeping pathways, elegant port de bras, and Tchaikovsky's unmistakable score.</p>

            <p class="form-question">Are you in Garland Waltz?</p>
            <v-radio-group v-model="garlandWaltz.participating">
              <v-radio label="Yes" :value="true"></v-radio>
              <v-radio label="No" :value="false"></v-radio>
            </v-radio-group>

            <span v-if="garlandWaltz.participating">
              <img src="/static/costume-vision-garland.png" alt="Costume Vision - Garland Waltz" class="costume-vision-img" />

              <p class="form-question">Which role?</p>
              <v-radio-group v-model="garlandWaltz.role" :rules="[v => !!v || 'Please select your role.']">
                <v-radio label="Ladies" value="Ladies"></v-radio>
                <v-radio label="Gentlemen" value="Gentlemen"></v-radio>
              </v-radio-group>

              <!-- Ladies -->
              <span v-if="garlandWaltz.role === 'Ladies'">
                <div class="costume-info-box">
                  <p class="costume-info-header">Costume Details - Garland Waltz Ladies</p>
                  <p><strong>Provided by IABF:</strong></p>
                  <ul>
                    <li>White Romantic Tutu (buy your own to keep or borrow from IABF)</li>
                    <li>Apron accessory & coordinated Garlands</li>
                  </ul>
                  <p><strong>You Provide:</strong></p>
                  <ul>
                    <li>Leotard + skirt (color and brand guidelines will be shared)</li>
                    <li>Pink or skin-tone tights with matching ballet flats or pointe shoes</li>
                  </ul>
                  <p class="cost-note"><i>If cost is prohibitive for any item or part of your costume, please reach out at <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.</i></p>
                </div>

                <span v-if="aliceWhiteTutuAnswered">
                  <div class="tutu-shared-notice">
                    You'll use the same white romantic tutu as Alice in Wonderland for this piece — no additional tutu needed!
                  </div>
                </span>
                <span v-else>
                  <p class="form-question">White Romantic Tutu:</p>
                  <div class="tutu-detail-box">
                    <img src="/static/tutu-white-romantic.jpg" alt="White Romantic Tutu" class="tutu-detail-img" />
                    <p>A white romantic tutu featuring several layers of soft tulle that hit below the knee, with an elastic waistband and panty. This beautiful romantic tutu floats and twirls with your every movement. You are welcome to borrow from IABF's costume wardrobe, or buy your own to take home and keep. If you borrow from our wardrobe, we will find the best fit based on your measurements. Your tutu will be waiting for you at IABF — no need to bring it yourself!</p>
                  </div>
                  <v-radio-group v-model="garlandWaltz.tutuChoice" :rules="[v => !!v || 'Please select a tutu option.']">
                    <v-radio label="Use my own from past IABF Waltz of Snowflakes or Giselle Wilis" value="own"></v-radio>
                    <v-radio label="Buy ($75)" value="buy"></v-radio>
                    <v-radio label="Borrow ($0)" value="borrow"></v-radio>
                  </v-radio-group>
                  <p v-if="garlandWaltz.tutuChoice === 'buy'" class="return-policy"><i>Tutus are custom made to order and cannot be returned, refunded, or exchanged.</i></p>

                  <span v-if="garlandWaltz.tutuChoice === 'buy' || garlandWaltz.tutuChoice === 'borrow'">
                    <p class="form-question">Measurements</p>
                    <v-radio-group v-model="sharedMeasurements.unit" row>
                      <v-radio label="Inches" value="inches"></v-radio>
                      <v-radio label="Centimeters" value="cm"></v-radio>
                    </v-radio-group>
                    <v-text-field v-model="sharedMeasurements.bust" label="Bust" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                      <template v-slot:append>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" small>mdi-information</v-icon>
                          </template>
                          <span>Measure around the fullest part of your bust</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                    <v-text-field v-model="sharedMeasurements.waist" label="Waist" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                      <template v-slot:append>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" small>mdi-information</v-icon>
                          </template>
                          <span>Measure around your natural waistline (narrowest part)</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                    <v-text-field v-model="sharedMeasurements.hip" label="Hip" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                      <template v-slot:append>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" small>mdi-information</v-icon>
                          </template>
                          <span>Measure around the fullest part of your hips</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                    <v-text-field v-model="sharedMeasurements.halfGirth" label="1/2 Girth" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                      <template v-slot:append>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" small>mdi-information</v-icon>
                          </template>
                          <span>Measure from your belly button through the legs up your lower back to the same height as the belly button</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                    <v-text-field v-model="sharedMeasurements.height" label="Height" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                      <template v-slot:append>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" small>mdi-information</v-icon>
                          </template>
                          <span>Your full standing height</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </span>
                </span>

                <p class="form-question">Ranked top 3 color choices for leotard and skirt overlay for your tutu:</p>
                <p class="note"><i>Colors will be assigned by mid-March.</i></p>
                <v-select v-model="garlandWaltz.colorPref1" :items="garlandColorOptionsFiltered1" label="1st Choice" :rules="[v => !!v || 'Required.']"></v-select>
                <v-select v-model="garlandWaltz.colorPref2" :items="garlandColorOptionsFiltered2" label="2nd Choice" :rules="[v => !!v || 'Required.']"></v-select>
                <v-select v-model="garlandWaltz.colorPref3" :items="garlandColorOptionsFiltered3" label="3rd Choice" :rules="[v => !!v || 'Required.']"></v-select>
              </span>

              <!-- Gentlemen -->
              <span v-if="garlandWaltz.role === 'Gentlemen'">
                <div class="costume-info-box">
                  <p class="costume-info-header">Costume Details - Garland Waltz Gentlemen</p>
                  <p><strong>Provided by IABF:</strong></p>
                  <ul>
                    <li>Cummerbund (structured, coordinated)</li>
                  </ul>
                  <p><strong>You Provide:</strong></p>
                  <ul>
                    <li>Poet's blouse (color and brand guidelines will be shared)</li>
                    <li>Black tights</li>
                    <li>Black ballet shoes</li>
                  </ul>
                  <p class="cost-note"><i>If cost is prohibitive for any item or part of your costume, please reach out at <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.</i></p>
                </div>

                <p class="form-question">Cummerbund measurement (waist):</p>
                <v-radio-group v-model="sharedMeasurements.unit" row>
                  <v-radio label="Inches" value="inches"></v-radio>
                  <v-radio label="Centimeters" value="cm"></v-radio>
                </v-radio-group>
                <v-text-field v-model="sharedMeasurements.waist" label="Waist" type="number" :suffix="sharedMeasurements.unit" :rules="[v => !!v || 'Required.']">
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small>mdi-information</v-icon>
                      </template>
                      <span>Measure around your natural waistline (narrowest part)</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </span>
            </span>

            <div v-if="measurementWarnings.length > 0 && garlandWaltz.participating && garlandWaltz.role" class="measurement-warnings">
              <p v-for="(warning, i) in measurementWarnings" :key="i">{{ warning }}</p>
            </div>

            <div class="step-nav">
              <v-btn @click="currentStep = 4">Back</v-btn>
              <v-btn class="primary" @click="goToStep(6, 'step5Form')">Next</v-btn>
            </div>
            </v-form>
          </span>

          <!-- Step 6: Check-in Questions -->
          <span v-if="currentStep === 6">
            <v-form ref="step6Form">
            <h3>Step 6 of 7: Check-in Questions</h3>

            <p class="form-question">Do you like to be in the front row?</p>
            <v-radio-group v-model="checkIn.frontRow" :rules="[v => !!v || 'Please answer this question.']">
              <v-radio label="Yes please!" value="Yes please"></v-radio>
              <v-radio label="I will if needed" value="I will if needed"></v-radio>
              <v-radio label="No thanks" value="No thanks"></v-radio>
            </v-radio-group>

            <p class="form-question">How are you feeling about the performance? (select all that apply)</p>
            <v-checkbox v-model="checkIn.feeling" label="Nervous" value="nervous"></v-checkbox>
            <v-checkbox v-model="checkIn.feeling" label="Super nervous" value="super nervous"></v-checkbox>
            <v-checkbox v-model="checkIn.feeling" label="Excited" value="excited"></v-checkbox>
            <v-checkbox v-model="checkIn.feeling" label="Overwhelmed" value="overwhelmed"></v-checkbox>
            <v-checkbox v-model="checkIn.feeling" label="Dream come true" value="dream come true"></v-checkbox>
            <v-input
              :value="checkIn.feeling && checkIn.feeling.length > 0 ? 'ok' : ''"
              :rules="[v => !!v || 'Please select at least one feeling.']"
              hide-details="auto"
            />

            <div class="step-nav">
              <v-btn @click="currentStep = 5">Back</v-btn>
              <v-btn class="primary" @click="goToStep(7, 'step6Form')">Review Order</v-btn>
            </div>
            </v-form>
          </span>

          <!-- Step 7: Summary & Payment -->
          <span v-if="currentStep === 7">
            <h3>Step 7 of 7: Summary & Payment</h3>

            <div class="order-summary">
              <h4>Your Costume Selections</h4>

              <div v-if="waltzOfHours.participating" class="summary-section">
                <p class="summary-piece-title"><strong>Waltz of the Hours</strong></p>
                <p v-if="waltzOfHours.tutuChoice === 'buy'">Pancake Tutu + Bodice (buy): $275</p>
                <p v-if="waltzOfHours.tutuChoice === 'borrow'">Pancake Tutu + Bodice (borrow): $0</p>
                <p v-if="waltzOfHours.tutuChoice === 'own'">Pancake Tutu + Bodice (own): $0</p>
                <p class="summary-note">IABF also provides: Matching Floral & Tulle Headpiece</p>
                <p class="summary-note">You provide: (optional) nude leotard, pink/skin-tone tights + matching shoes</p>
              </div>

              <div v-if="aliceInWonderland.participating" class="summary-section">
                <p class="summary-piece-title"><strong>Alice in Wonderland ({{ aliceInWonderland.role }})</strong></p>
                <span v-if="aliceInWonderland.role === 'Queen of Hearts'">
                  <p>Costume to be coordinated individually</p>
                </span>
                <span v-else-if="aliceInWonderland.role !== 'Men & Jokers'">
                  <p v-if="aliceInWonderland.tutuChoice === 'buy'">{{ aliceInWonderland.role === 'Pointe' ? 'White Pancake' : 'White Romantic' }} Tutu (buy): $75</p>
                  <p v-if="aliceInWonderland.tutuChoice === 'borrow'">{{ aliceInWonderland.role === 'Pointe' ? 'White Pancake' : 'White Romantic' }} Tutu (borrow): $0</p>
                  <p v-if="aliceInWonderland.tutuChoice === 'own'">{{ aliceInWonderland.role === 'Pointe' ? 'White Pancake' : 'White Romantic' }} Tutu (own): $0</p>
                </span>
                <p class="summary-note">IABF also provides: Playing-Card Overlay Accessories + Coordinated Headpiece</p>
                <p v-if="aliceInWonderland.role !== 'Men & Jokers'" class="summary-note">You provide: white leotard, pink/skin-tone tights + matching shoes</p>
                <p v-if="aliceInWonderland.role === 'Men & Jokers'" class="summary-note">You provide: black tights, white ballet shirt, black ballet flats</p>
              </div>

              <div v-if="leJazzHot.participating" class="summary-section">
                <p class="summary-piece-title"><strong>Le Jazz Hot</strong></p>
                <p>Self-costumed: $0</p>
                <p class="summary-note">You provide: all black head-to-toe (top, pants/leggings/shorts, black jazz or ballet shoes)</p>
              </div>

              <div v-if="becoming.participating" class="summary-section">
                <p class="summary-piece-title"><strong>Becoming</strong></p>
                <p v-if="becoming.topChoice === 'crop'">Crop top (buy): $50 — Size: {{ becoming.cropTopSize }}</p>
                <p v-if="becoming.topChoice === 'crop_ship'">Crop top (buy + ship): $65 — Size: {{ becoming.cropTopSize }}</p>
                <p v-if="becoming.topChoice === 'leotard'">Leotard: you provide (guidelines sent later)</p>
                <p v-if="becoming.skirtChoice === 'buy'">Blue Flowing Skirt (buy): $65 — Size: {{ becoming.skirtSize }}</p>
                <p v-if="becoming.skirtChoice === 'borrow'">Blue Flowing Skirt (borrow): $0 — Size: {{ becoming.skirtSize }}</p>
              </div>

              <div v-if="garlandWaltz.participating" class="summary-section">
                <p class="summary-piece-title"><strong>Garland Waltz ({{ garlandWaltz.role }})</strong></p>
                <span v-if="garlandWaltz.role === 'Ladies'">
                  <span v-if="aliceWhiteTutuAnswered">
                    <p>White Romantic Tutu: same as Alice in Wonderland</p>
                  </span>
                  <span v-else-if="garlandWaltz.tutuChoice">
                    <p v-if="garlandWaltz.tutuChoice === 'buy'">White Romantic Tutu (buy): $75</p>
                    <p v-if="garlandWaltz.tutuChoice === 'borrow'">White Romantic Tutu (borrow): $0</p>
                    <p v-if="garlandWaltz.tutuChoice === 'own'">White Romantic Tutu (own): $0</p>
                  </span>
                  <p class="summary-note">IABF also provides: Apron + Garlands</p>
                  <p class="summary-note">You provide: leotard + skirt, pink/skin-tone tights + matching shoes</p>
                </span>
                <span v-if="garlandWaltz.role === 'Gentlemen'">
                  <p class="summary-note">IABF provides: Cummerbund</p>
                  <p class="summary-note">You provide: poet's blouse, black tights, black ballet shoes</p>
                </span>
              </div>

              <hr />
              <h4>Total: ${{ calculatedTotal }}</h4>

              <p v-if="hasBuyOrBorrowItem" class="summary-note" style="margin-top: 12px;">Your tutu(s) and/or skirt will be waiting for you at IABF — no need to bring them yourself!</p>

              <div v-if="buyingPancakeTutu" class="tutu-travel-tip">
                <p><strong>Traveling home with your pancake tutu?</strong></p>
                <p>We recommend bringing a tutu bag to safely carry your tutu home. <a href="https://danznmotionshop.com/products/pro-tutu-bag?variant=13918117888042&country=US&currency=USD" target="_blank">Shop tutu bags here.</a></p>
                <p>Watch our <a href="https://app.brocheballetonline.com/m/mOFdaPHI" target="_blank">How to Travel with a Tutu</a> video for packing tips!</p>
              </div>
            </div>

            <span v-if="calculatedTotal > 0">
              <p class="form-question">How would you like to handle payment?</p>
              <v-btn class="primary mr-3" @click="payNow" :loading="paymentLoading">
                Pay Now (${{ calculatedTotal }})
              </v-btn>
              <v-btn @click="addToNextPayment">
                Add to Next IABF Payment
              </v-btn>
            </span>
            <span v-else>
              <v-btn class="primary" @click="submitOrder">
                Submit Costume Order
              </v-btn>
            </span>

            <div class="step-nav mt-4">
              <v-btn @click="currentStep = 6">Back</v-btn>
            </div>
          </span>

          <!-- Success / Confirmation -->
          <span v-if="currentStep === 8">
            <h2>Costume Order Submitted!</h2>
            <p>Thank you, {{ firstName }}! Your costume order has been saved.</p>
            <p v-if="costumeOrderPaid">Your payment has been received.</p>
            <p v-else-if="$route.query.session_id && !costumeOrderPaid">Your payment is being processed. You'll see it confirmed shortly.</p>
            <p v-else-if="costumeTotalCost > 0">Your costume costs (${{ costumeTotalCost }}) will be added to your next IABF payment.</p>
            <p><i>Please contact <a href="mailto:hello@iabf.dance">hello@iabf.dance</a> by February 28 to make any changes.</i></p>
          </span>

        </span>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { IABF_DANCER_YEAR, hasCorpsCostumeOrder } from '@/config/iabf-event'

export default {
  props: ['id'],
  data () {
    return {
      loading: false,
      dancerId: null,
      dancerIdentified: false,
      dancerEmail: '',
      firstName: '',
      lookupEmail: '',
      lookupLast4: '',
      lookupError: false,
      saveError: '',
      currentStep: 0,
      costumeOrderCompleted: false,
      costumeOrderPaid: false,
      costumeTotalCost: 0,
      paymentLoading: false,
      addedToNextPaymentConfirmed: false,
      notInCorps: false,
      assignedWaltzColor: '',
      assignedGarlandColor: '',
      becomingSizeChartUnit: 'inches',
      waltzColorOptions: ['Yellow', 'Pink', 'Blue', 'Green', 'Purple'],
      garlandColorOptions: ['Light Purple', 'Pink', 'Dark Purple'],
      waltzOfHours: {
        participating: false,
        tutuChoice: '',
        tutuColor: '',
        colorPref1: '',
        colorPref2: '',
        colorPref3: '',
        learnedOnDemand: '',
        pirouetteChoice: ''
      },
      aliceInWonderland: {
        participating: false,
        role: '',
        tutuChoice: '',
        costumeChoice: ''
      },
      leJazzHot: {
        participating: false
      },
      becoming: {
        participating: false,
        topChoice: '',
        skirtChoice: '',
        cropTopSize: '',
        skirtSize: '',
        shippingName: '',
        shippingAddress1: '',
        shippingAddress2: '',
        shippingCity: '',
        shippingState: '',
        shippingZip: ''
      },
      garlandWaltz: {
        participating: false,
        role: '',
        tutuChoice: '',
        tutuSharedWithAlice: false,
        colorPref1: '',
        colorPref2: '',
        colorPref3: ''
      },
      checkIn: {
        frontRow: '',
        feeling: [],
        questions: ''
      },
      sharedMeasurements: {
        unit: 'inches',
        bust: '',
        waist: '',
        hip: '',
        girth: '',
        halfGirth: '',
        height: ''
      }
    }
  },
  computed: {
    currentIABFDancer () {
      return this.$store.getters.currentIABFDancer
    },
    costumeOrderOpen () {
      const year = Number(this.$store.getters.iabfDancerYear) || IABF_DANCER_YEAR
      return hasCorpsCostumeOrder(year)
    },
    lookupFormValid () {
      return this.lookupEmail !== '' && this.lookupLast4.length === 4
    },
    aliceWhiteTutuAnswered () {
      if (!this.aliceInWonderland.participating) return false
      if (this.aliceInWonderland.role === 'Flat Ladies') return true
      return false
    },
    buyingPancakeTutu () {
      if (this.waltzOfHours.participating && this.waltzOfHours.tutuChoice === 'buy') return true
      if (this.aliceInWonderland.participating && this.aliceInWonderland.role === 'Pointe' && this.aliceInWonderland.tutuChoice === 'buy') return true
      return false
    },
    hasBuyOrBorrowItem () {
      if (this.waltzOfHours.participating && (this.waltzOfHours.tutuChoice === 'buy' || this.waltzOfHours.tutuChoice === 'borrow')) return true
      if (this.aliceInWonderland.participating && this.aliceInWonderland.role !== 'Men & Jokers' && this.aliceInWonderland.role !== 'Queen of Hearts') {
        if (this.aliceInWonderland.tutuChoice === 'buy' || this.aliceInWonderland.tutuChoice === 'borrow') return true
      }
      if (this.becoming.participating && (this.becoming.skirtChoice === 'buy' || this.becoming.skirtChoice === 'borrow')) return true
      if (this.garlandWaltz.participating && this.garlandWaltz.role === 'Ladies') {
        if (this.aliceWhiteTutuAnswered) return true
        if (this.garlandWaltz.tutuChoice === 'buy' || this.garlandWaltz.tutuChoice === 'borrow') return true
      }
      return false
    },
    measurementWarnings () {
      const warnings = []
      const m = this.sharedMeasurements
      const isInches = m.unit === 'inches'
      const bust = parseFloat(m.bust)
      const waist = parseFloat(m.waist)
      const hip = parseFloat(m.hip)
      const girth = parseFloat(m.girth)
      const halfGirth = parseFloat(m.halfGirth)
      const height = parseFloat(m.height)

      // Determine which measurements are relevant for the current step
      let checkBust = false, checkWaist = false, checkHip = false, checkGirth = false, checkHalfGirth = false, checkHeight = false

      if (this.currentStep === 1 && this.waltzOfHours.participating && (this.waltzOfHours.tutuChoice === 'buy' || this.waltzOfHours.tutuChoice === 'borrow')) {
        checkBust = true; checkWaist = true; checkHip = true; checkGirth = true; checkHeight = true
      }
      if (this.currentStep === 2 && this.aliceInWonderland.participating) {
        if (this.aliceInWonderland.role === 'Pointe' && (this.aliceInWonderland.tutuChoice === 'buy' || this.aliceInWonderland.tutuChoice === 'borrow')) {
          checkWaist = true; checkHip = true; checkHalfGirth = true; checkHeight = true
        }
        if (this.aliceInWonderland.role === 'Flat Ladies' && (this.aliceInWonderland.tutuChoice === 'buy' || this.aliceInWonderland.tutuChoice === 'borrow')) {
          checkWaist = true; checkHip = true; checkHalfGirth = true; checkHeight = true
        }
      }
      // Becoming no longer uses shared measurements (uses size chart instead)
      if (this.currentStep === 5 && this.garlandWaltz.participating) {
        if (this.garlandWaltz.role === 'Ladies' && !this.aliceWhiteTutuAnswered && (this.garlandWaltz.tutuChoice === 'buy' || this.garlandWaltz.tutuChoice === 'borrow')) {
          checkBust = true; checkWaist = true; checkHip = true; checkHalfGirth = true; checkHeight = true
        }
        if (this.garlandWaltz.role === 'Gentlemen') {
          checkWaist = true
        }
      }

      if (checkWaist && checkHip && waist && hip && waist >= hip) {
        warnings.push('Your waist measurement is equal to or larger than your hip measurement — please double-check.')
      }
      if (checkBust && checkWaist && bust && waist && waist > bust) {
        warnings.push('Your waist measurement is larger than your bust measurement — please double-check.')
      }
      if (isInches) {
        if (checkBust && bust && (bust < 25 || bust > 65)) warnings.push('Bust measurement seems unusual for inches — please double-check.')
        if (checkWaist && waist && (waist < 20 || waist > 58)) warnings.push('Waist measurement seems unusual for inches — please double-check.')
        if (checkHip && hip && (hip < 28 || hip > 70)) warnings.push('Hip measurement seems unusual for inches — please double-check.')
        if (checkGirth && girth && (girth < 50 || girth > 100)) warnings.push('Girth measurement seems unusual for inches — please double-check.')
        if (checkHalfGirth && halfGirth && (halfGirth < 24 || halfGirth > 55)) warnings.push('1/2 Girth measurement seems unusual for inches — please double-check.')
        if (checkHeight && height && (height < 55 || height > 78)) warnings.push('Height measurement seems unusual for inches — please double-check. (Example: 5\'6" = 66 inches)')
      } else {
        if (checkBust && bust && (bust < 65 || bust > 165)) warnings.push('Bust measurement seems unusual for centimeters — please double-check.')
        if (checkWaist && waist && (waist < 50 || waist > 148)) warnings.push('Waist measurement seems unusual for centimeters — please double-check.')
        if (checkHip && hip && (hip < 70 || hip > 178)) warnings.push('Hip measurement seems unusual for centimeters — please double-check.')
        if (checkGirth && girth && (girth < 125 || girth > 255)) warnings.push('Girth measurement seems unusual for centimeters — please double-check.')
        if (checkHalfGirth && halfGirth && (halfGirth < 60 || halfGirth > 140)) warnings.push('1/2 Girth measurement seems unusual for centimeters — please double-check.')
        if (checkHeight && height && (height < 140 || height > 200)) warnings.push('Height measurement seems unusual for centimeters — please double-check.')
      }
      return warnings
    },
    waltzColorOptions1 () {
      return this.waltzColorOptions.filter(c => c !== this.waltzOfHours.colorPref2 && c !== this.waltzOfHours.colorPref3)
    },
    waltzColorOptions2 () {
      return this.waltzColorOptions.filter(c => c !== this.waltzOfHours.colorPref1 && c !== this.waltzOfHours.colorPref3)
    },
    waltzColorOptions3 () {
      return this.waltzColorOptions.filter(c => c !== this.waltzOfHours.colorPref1 && c !== this.waltzOfHours.colorPref2)
    },
    garlandColorOptionsFiltered1 () {
      return this.garlandColorOptions.filter(c => c !== this.garlandWaltz.colorPref2 && c !== this.garlandWaltz.colorPref3)
    },
    garlandColorOptionsFiltered2 () {
      return this.garlandColorOptions.filter(c => c !== this.garlandWaltz.colorPref1 && c !== this.garlandWaltz.colorPref3)
    },
    garlandColorOptionsFiltered3 () {
      return this.garlandColorOptions.filter(c => c !== this.garlandWaltz.colorPref1 && c !== this.garlandWaltz.colorPref2)
    },
    becomingSizeOptions () {
      return [
        { text: 'T (Teen)', value: 'T' },
        { text: 'SA (Small Adult)', value: 'SA' },
        { text: 'MA (Medium Adult)', value: 'MA' },
        { text: 'LA (Large Adult)', value: 'LA' },
        { text: 'XLA (Extra Large Adult)', value: 'XLA' }
      ]
    },
    calculatedTotal () {
      let total = 0
      if (this.waltzOfHours.participating && this.waltzOfHours.tutuChoice === 'buy') {
        total += 275
      }
      if (this.aliceInWonderland.participating && this.aliceInWonderland.role !== 'Men & Jokers' && this.aliceInWonderland.role !== 'Queen of Hearts') {
        if (this.aliceInWonderland.tutuChoice === 'buy') total += 75
      }
      if (this.becoming.participating) {
        if (this.becoming.topChoice === 'crop') total += 50
        if (this.becoming.topChoice === 'crop_ship') total += 65
        if (this.becoming.skirtChoice === 'buy') total += 65
      }
      if (this.garlandWaltz.participating && this.garlandWaltz.role === 'Ladies' && !this.aliceWhiteTutuAnswered) {
        if (this.garlandWaltz.tutuChoice === 'buy') total += 75
      }
      return total
    },
    formState () {
      return {
        currentStep: this.currentStep,
        waltzOfHours: this.waltzOfHours,
        aliceInWonderland: this.aliceInWonderland,
        leJazzHot: this.leJazzHot,
        becoming: this.becoming,
        garlandWaltz: this.garlandWaltz,
        checkIn: this.checkIn,
        sharedMeasurements: this.sharedMeasurements
      }
    }
  },
  watch: {
    id (newId) {
      if (newId && !this.dancerIdentified) {
        this.dancerId = newId
        if (this.$route.query.session_id) {
          this.loading = true
          this.pollForPaymentConfirmation(this.dancerId)
        } else {
          this.loadDancer()
        }
      }
    },
    formState: {
      deep: true,
      handler (val) {
        if (this.dancerId && this.dancerIdentified && !this.costumeOrderCompleted) {
          localStorage.setItem('costumeOrder_' + this.dancerId, JSON.stringify(val))
        }
      }
    },
    'waltzOfHours.colorPref1' (val) {
      if (val && this.waltzOfHours.colorPref2 === val) this.waltzOfHours.colorPref2 = ''
      if (val && this.waltzOfHours.colorPref3 === val) this.waltzOfHours.colorPref3 = ''
    },
    'waltzOfHours.colorPref2' (val) {
      if (val && this.waltzOfHours.colorPref1 === val) this.waltzOfHours.colorPref1 = ''
      if (val && this.waltzOfHours.colorPref3 === val) this.waltzOfHours.colorPref3 = ''
    },
    'waltzOfHours.colorPref3' (val) {
      if (val && this.waltzOfHours.colorPref1 === val) this.waltzOfHours.colorPref1 = ''
      if (val && this.waltzOfHours.colorPref2 === val) this.waltzOfHours.colorPref2 = ''
    },
    'garlandWaltz.colorPref1' (val) {
      if (val && this.garlandWaltz.colorPref2 === val) this.garlandWaltz.colorPref2 = ''
      if (val && this.garlandWaltz.colorPref3 === val) this.garlandWaltz.colorPref3 = ''
    },
    'garlandWaltz.colorPref2' (val) {
      if (val && this.garlandWaltz.colorPref1 === val) this.garlandWaltz.colorPref1 = ''
      if (val && this.garlandWaltz.colorPref3 === val) this.garlandWaltz.colorPref3 = ''
    },
    'garlandWaltz.colorPref3' (val) {
      if (val && this.garlandWaltz.colorPref1 === val) this.garlandWaltz.colorPref1 = ''
      if (val && this.garlandWaltz.colorPref2 === val) this.garlandWaltz.colorPref2 = ''
    }
  },
  created () {
    if (this.$route.query.embed) {
      this.$store.commit('setLayout', 'embed-layout')
    } else {
      this.$store.commit('setLayout', 'iabf-layout')
    }

    // Check for direct ID access via route param
    if (this.id) {
      this.dancerId = this.id
      // Handle Stripe success return
      if (this.$route.query.session_id) {
        this.loading = true
        this.pollForPaymentConfirmation(this.dancerId)
      } else {
        this.loadDancer()
      }
    } else if (this.currentIABFDancer && this.currentIABFDancer.id) {
      // Already logged in via magic link — use stored dancer
      this.dancerId = this.currentIABFDancer.id
      this.loadDancer()
    }
  },
  methods: {
    goToStep (target, formRef) {
      if (formRef && this.$refs[formRef]) {
        if (!this.$refs[formRef].validate()) {
          // Vuetify field errors show; keep user on this step
          return
        }
      }
      this.currentStep = target
    },
    onLookUp () {
      if (!this.lookupFormValid) return
      this.loading = true
      this.lookupError = false
      this.$store.dispatch('lookUpIABFDancer', {
        email: this.lookupEmail.toLowerCase().trim(),
        last4: this.lookupLast4
      }).then(() => {
        if (this.currentIABFDancer && this.currentIABFDancer.id) {
          const embedQuery = this.$route.query.embed ? '?embed=true' : ''
          this.$router.push('/costume-order/' + this.currentIABFDancer.id + embedQuery)
        } else {
          this.loading = false
          this.lookupError = true
        }
      }).catch(() => {
        this.loading = false
        this.lookupError = true
      })
    },
    loadDancer () {
      this.loading = true
      this.$store.dispatch('loadIABFDancer', this.dancerId).then(() => {
        this.populateFromDancer()
        if (!this.costumeOrderCompleted) {
          this.restoreFromLocalStorage()
        }
        this.loading = false
        this.dancerIdentified = true
      }).catch(() => {
        this.loading = false
        this.lookupError = true
        this.dancerIdentified = false
      })
    },
    restoreFromLocalStorage () {
      try {
        const saved = localStorage.getItem('costumeOrder_' + this.dancerId)
        if (saved) {
          const data = JSON.parse(saved)
          if (data.currentStep) this.currentStep = data.currentStep
          if (data.waltzOfHours) this.waltzOfHours = { ...this.waltzOfHours, ...data.waltzOfHours }
          if (data.aliceInWonderland) this.aliceInWonderland = { ...this.aliceInWonderland, ...data.aliceInWonderland }
          if (data.leJazzHot) this.leJazzHot = { ...this.leJazzHot, ...data.leJazzHot }
          if (data.becoming) this.becoming = { ...this.becoming, ...data.becoming }
          if (data.garlandWaltz) this.garlandWaltz = { ...this.garlandWaltz, ...data.garlandWaltz }
          if (data.checkIn) this.checkIn = { ...this.checkIn, ...data.checkIn }
          if (data.sharedMeasurements) this.sharedMeasurements = { ...this.sharedMeasurements, ...data.sharedMeasurements }
        }
      } catch (e) {
        // ignore parse errors
      }
    },
    clearLocalStorage () {
      if (this.dancerId) {
        localStorage.removeItem('costumeOrder_' + this.dancerId)
      }
    },
    populateFromDancer () {
      const d = this.currentIABFDancer
      this.firstName = d.firstName || ''
      this.dancerEmail = d.email || ''
      this.costumeOrderCompleted = d.costumeOrderCompleted || false
      this.costumeOrderPaid = d.costumeOrderPaid || false
      this.costumeTotalCost = d.costumeTotalCost || 0

      this.notInCorps = d.notInCorps || false
      this.assignedWaltzColor = d.assignedWaltzColor || ''
      this.assignedGarlandColor = d.assignedGarlandColor || ''

      if (d.costumeOrderCompleted) {
        if (d.waltzOfHours) this.waltzOfHours = { ...this.waltzOfHours, ...d.waltzOfHours }
        if (d.aliceInWonderland) this.aliceInWonderland = { ...this.aliceInWonderland, ...d.aliceInWonderland }
        if (d.leJazzHot) this.leJazzHot = { ...this.leJazzHot, ...d.leJazzHot }
        if (d.becoming) this.becoming = { ...this.becoming, ...d.becoming }
        if (d.garlandWaltz) this.garlandWaltz = { ...this.garlandWaltz, ...d.garlandWaltz }
        if (d.checkIn) this.checkIn = { ...this.checkIn, ...d.checkIn }
        if (d.sharedMeasurements) this.sharedMeasurements = { ...this.sharedMeasurements, ...d.sharedMeasurements }
      }
    },
    buildOrderPayload () {
      this.garlandWaltz.tutuSharedWithAlice = this.aliceWhiteTutuAnswered
      return {
        id: this.dancerId,
        costumeOrderCompleted: true,
        costumeOrderPaid: this.costumeOrderPaid,
        costumeTotalCost: this.calculatedTotal,
        waltzOfHours: { ...this.waltzOfHours },
        aliceInWonderland: { ...this.aliceInWonderland },
        leJazzHot: { ...this.leJazzHot },
        becoming: { ...this.becoming },
        garlandWaltz: { ...this.garlandWaltz },
        checkIn: { ...this.checkIn },
        sharedMeasurements: { ...this.sharedMeasurements }
      }
    },
    submitOrder () {
      this.loading = true
      this.saveError = ''
      const payload = this.buildOrderPayload()
      this.$store.dispatch('saveCostumeOrder', payload).then(() => {
        this.clearLocalStorage()
        this.costumeOrderCompleted = true
        this.costumeTotalCost = this.calculatedTotal
        this.loading = false
        this.currentStep = 8
      }).catch((err) => {
        console.error('saveCostumeOrder failed:', err)
        this.loading = false
        this.saveError = (err && err.message && String(err.message).toLowerCase().includes('permission'))
          ? 'Could not save — your login may not match this registration. Email hello@iabf.dance for help.'
          : 'Could not save your costume order. Please try again.'
        alert(this.saveError)
      })
    },
    markAddedToNextPayment () {
      this.addedToNextPaymentConfirmed = true
      this.$store.dispatch('assignCostumeColor', { id: this.dancerId, field: 'costumePaymentMethod', color: 'addToNext' })
    },
    addToNextPayment () {
      this.costumeOrderPaid = false
      this.$store.dispatch('assignCostumeColor', { id: this.dancerId, field: 'costumePaymentMethod', color: 'addToNext' })
      this.submitOrder()
    },
    pollForPaymentConfirmation (dancerId) {
      let attempts = 0
      const maxAttempts = 10
      const poll = () => {
        this.$store.dispatch('loadIABFDancer', dancerId).then(() => {
          const dancer = this.$store.state.iabfDancer
          this.populateFromDancer()
          if (dancer && dancer.costumeOrderPaid) {
            this.costumeOrderPaid = true
            localStorage.removeItem('costumeOrder_' + dancerId)
            this.loading = false
            this.currentStep = 8
          } else if (attempts < maxAttempts) {
            attempts++
            setTimeout(poll, 2000)
          } else {
            // Webhook hasn't fired yet but Stripe redirected — show confirmation with pending status
            this.costumeOrderPaid = false
            localStorage.removeItem('costumeOrder_' + dancerId)
            this.loading = false
            this.currentStep = 8
          }
        })
      }
      poll()
    },
    payNow () {
      this.paymentLoading = true
      this.saveError = ''
      const payload = this.buildOrderPayload()
      this.$store.dispatch('saveCostumeOrder', payload).then(() => {
        const lineItems = []
        if (this.waltzOfHours.participating && this.waltzOfHours.tutuChoice === 'buy') {
          lineItems.push({ description: 'Waltz of the Hours - Pancake Tutu + Bodice', amount: 275 })
        }
        if (this.aliceInWonderland.participating) {
          if (this.aliceInWonderland.role !== 'Men & Jokers' && this.aliceInWonderland.role !== 'Queen of Hearts' && this.aliceInWonderland.tutuChoice === 'buy') {
            lineItems.push({ description: 'Alice - White Tutu', amount: 75 })
          }
        }
        if (this.becoming.participating) {
          if (this.becoming.topChoice === 'crop') {
            lineItems.push({ description: 'Becoming - Crop Top', amount: 50 })
          }
          if (this.becoming.topChoice === 'crop_ship') {
            lineItems.push({ description: 'Becoming - Crop Top + Shipping', amount: 65 })
          }
          if (this.becoming.skirtChoice === 'buy') {
            lineItems.push({ description: 'Becoming - Blue Flowing Skirt', amount: 65 })
          }
        }
        if (this.garlandWaltz.participating && this.garlandWaltz.role === 'Ladies' && !this.aliceWhiteTutuAnswered && this.garlandWaltz.tutuChoice === 'buy') {
          lineItems.push({ description: 'Garland Waltz - White Romantic Tutu', amount: 75 })
        }

        const baseUrl = window.location.origin
        this.$store.dispatch('createCostumeCheckoutSession', {
          dancerId: this.dancerId,
          lineItems: lineItems,
          successUrl: baseUrl + '/iabf/costume-order/' + this.dancerId + '/success?session_id={CHECKOUT_SESSION_ID}' + (this.$route.query.embed ? '&embed=true' : ''),
          cancelUrl: baseUrl + '/iabf/costume-order/' + this.dancerId + (this.$route.query.embed ? '?embed=true' : '')
        }).then((checkoutUrl) => {
          window.location.href = checkoutUrl
        }).catch(() => {
          this.paymentLoading = false
          alert('There was an error creating the payment session. Please try again or choose "Add to Next IABF Payment".')
        })
      }).catch((err) => {
        console.error('saveCostumeOrder before pay failed:', err)
        this.paymentLoading = false
        const msg = (err && err.message && String(err.message).toLowerCase().includes('permission'))
          ? 'Could not save order before payment — your login may not match this registration. Email hello@iabf.dance for help.'
          : 'Could not save your costume order before payment. Please try again.'
        this.saveError = msg
        alert(msg)
      })
    },
    resetOrder () {
      this.loading = true
      this.$store.dispatch('saveCostumeOrder', {
        id: this.dancerId,
        costumeOrderCompleted: false,
        costumeOrderPaid: false,
        costumeTotalCost: 0,
        waltzOfHours: null,
        aliceInWonderland: null,
        leJazzHot: null,
        becoming: null,
        garlandWaltz: null,
        checkIn: null,
        sharedMeasurements: null
      }).then(() => {
        this.clearLocalStorage()
        this.costumeOrderCompleted = false
        this.costumeOrderPaid = false
        this.costumeTotalCost = 0
        this.currentStep = 0
        this.waltzOfHours = { participating: false, tutuChoice: '', tutuColor: '', colorPref1: '', colorPref2: '', colorPref3: '', learnedOnDemand: '', pirouetteChoice: '' }
        this.aliceInWonderland = { participating: false, role: '', tutuChoice: '', costumeChoice: '' }
        this.leJazzHot = { participating: false }
        this.becoming = { participating: false, topChoice: '', skirtChoice: '', cropTopSize: '', skirtSize: '', shippingName: '', shippingAddress1: '', shippingAddress2: '', shippingCity: '', shippingState: '', shippingZip: '' }
        this.garlandWaltz = { participating: false, role: '', tutuChoice: '', tutuSharedWithAlice: false, colorPref1: '', colorPref2: '', colorPref3: '' }
        this.checkIn = { frontRow: '', feeling: [], questions: '' }
        this.sharedMeasurements = { unit: 'inches', bust: '', waist: '', hip: '', girth: '', halfGirth: '', height: '' }
        this.loading = false
      })
    },
    tutuChoiceLabel (choice) {
      if (choice === 'own') return 'Using own'
      if (choice === 'buy') return 'Buying'
      if (choice === 'borrow') return 'Borrowing'
      return choice
    },
    queenCostumeLabel () {
      return 'To be coordinated individually'
    }
  }
}
</script>

<style scoped>
.back-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
  transition: color 0.15s;
  display: inline-block;
  margin-bottom: 8px;
}

.back-link:hover {
  color: var(--iabf-ink, #1c1b1f);
}

h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 32px;
  letter-spacing: -.02em;
  line-height: 1.2em;
  padding: 20px 0 5px 0;
}

h3 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 500;
  font-size: 24px;
  padding: 20px 0 5px 0;
  border-bottom: 1px solid rgba(40,26,23,.2);
  margin-bottom: 10px;
}

h4 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 500;
  font-size: 20px;
  padding: 10px 0 5px 0;
}

p, li {
  font-family: var(--iabf-font-body, inherit);
  font-weight: 400;
  font-size: 19px;
  letter-spacing: .01em;
  line-height: 1.7em;
  color: rgba(40,26,23,.6);
}

.form-question {
  padding-top: 10px;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 500;
  font-size: 19px;
  color: rgba(40,26,23,.85);
}

.piece-tagline {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-style: italic;
  font-size: 20px;
  color: rgba(40,26,23,.7);
  margin-bottom: 0;
  padding-bottom: 0;
}

.piece-description {
  font-style: italic;
  padding: 5px 0 10px 0;
}

.note {
  font-size: 16px;
  font-style: italic;
}

.costume-info-box {
  background: #ffffff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 20px 22px;
  margin: 10px 0 20px 0;
  color: var(--iabf-ink, #1c1b1f);
}

.costume-info-box ul {
  margin-top: 2px;
  margin-bottom: 8px;
}

.tutu-shared-notice {
  background: #ffffff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 16px 20px;
  margin: 10px 0 20px 0;
  font-family: var(--iabf-font-body, inherit);
  font-weight: 600;
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
}

.measurement-warnings {
  background: #ffffff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 14px 18px;
  margin: 10px 0;
}

.measurement-warnings p {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
  margin: 4px 0;
  line-height: 1.4em;
}

.cost-note {
  font-size: 16px;
  font-style: italic;
  color: rgba(40,26,23,.5);
  margin-top: 8px;
  margin-bottom: 0;
}

.costume-info-header {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 600;
  font-size: 18px;
  color: rgba(40,26,23,.85);
  margin-bottom: 8px;
}

.step-nav {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid rgba(40,26,23,.1);
}


.order-summary, .order-summary-readonly {
  background: #faf9f7;
  padding: 20px;
  border-radius: 8px;
  margin: 15px 0;
}

.summary-section {
  margin-bottom: 15px;
}

.summary-piece-title {
  margin-bottom: 2px;
}

.summary-note {
  font-size: 15px;
  font-style: italic;
  color: rgba(40,26,23,.5);
  margin: 0;
  line-height: 1.5em;
}

hr {
  margin: 15px 0;
  border-color: rgba(40,26,23,.15);
}

.costume-vision-img {
  width: 100%;
  max-width: 500px;
  border-radius: 8px;
  margin: 10px 0 15px 0;
  box-shadow: 0 2px 8px rgba(0,0,0,.1);
}

.tutu-detail-box {
  margin: 10px 0 15px 0;
}

.tutu-detail-box p {
  font-size: 16px;
  line-height: 1.6em;
  color: rgba(40,26,23,.65);
  margin-top: 8px;
}

.tutu-detail-img {
  width: 100%;
  max-width: 400px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,.1);
}

.tutu-travel-tip {
  background: #e8f5f4;
  border-left: 3px solid var(--iabf-primary, #8a957d);
  padding: 12px 18px;
  border-radius: 4px;
  margin: 15px 0 5px 0;
}

.tutu-travel-tip p {
  font-size: 16px;
  line-height: 1.5em;
  margin: 4px 0;
}

.tutu-travel-tip a {
  color: var(--iabf-primary, #8a957d);
  font-weight: 500;
}

.return-policy {
  font-size: 15px;
  color: #b71c1c;
  margin-top: -8px;
  margin-bottom: 12px;
}

.size-chart-container {
  margin: 10px 0 15px 0;
}

.size-chart-img {
  width: 100%;
  max-width: 600px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,.1);
}
</style>
